import { type ReactNode, useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { cx } from "class-variance-authority";

import * as styles from "./ShinyText.module.scss";

type ShinyTextProps = {
  children?: ReactNode;
  isEnabled?: boolean;
  duration?: number;
  delay?: number;
  repeatDelay?: number;
  className?: string;
  overlayClassName?: string;
  /** Increment to fire the animation once. When omitted, the intersection observer controls looping. */
  trigger?: number;
};

const ShinyText = ({
  children,
  isEnabled = true,
  duration = 4,
  delay = 0,
  repeatDelay = 0,
  className,
  overlayClassName,
  trigger,
}: ShinyTextProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    const obs = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.2 },
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [isEnabled]);

  if (!isEnabled) {
    return <>{children}</>;
  }

  return (
    <div ref={containerRef} className={cx(styles.shinyTextWrapper, className)}>
      <span>{children}</span>
      <motion.span
        key={trigger}
        className={cx(styles.shinyTextOverlay, overlayClassName)}
        animate={
          trigger !== undefined && isVisible
            ? { backgroundPositionX: ["200%", "-200%"] }
            : isVisible
              ? { backgroundPositionX: ["200%", "-200%"] }
              : false
        }
        transition={{
          duration,
          repeat: trigger !== undefined ? 0 : Infinity,
          delay,
          repeatDelay,
          ease: "linear",
        }}
      >
        {children}
      </motion.span>
    </div>
  );
};

export { ShinyText };
